import { z } from 'zod';

export const ElementSchema = z.enum([
  'fire', 'water', 'nature', 'elektro', 'light', 'shadow', 'neutral'
]);

export const RaritySchema = z.enum(['common', 'rare', 'epic', 'legendary']);

export const TowerCategorySchema = z.enum(['attack', 'terrain', 'wall']);

export const UpgradePathIdSchema = z.enum(['A', 'B']);

export const TowerStatsSchema = z.object({
  dmg: z.number().min(0),
  spd: z.number().min(0),
  range: z.number().min(0),
  crit: z.number().min(0).max(1),
  splash: z.number().positive().optional(),
  slow: z.number().min(0).max(1).optional(),
  chain: z.number().int().min(0).optional(),
  income: z.number().min(0).optional(),
  terrain: z.boolean().optional(),
  projSpeed: z.number().min(0).optional(), // pixels/sec; 0 = no projectile (terrain/wall)
});

export const UpgradePathDefSchema = z.object({
  id: UpgradePathIdSchema,
  name: z.string().min(1),
  desc: z.string().min(1),
  effect: z.string().min(1),
});

export const TowerUpgradeSchema = z.object({
  cost: z.number().int().positive(),
  desc: z.string().min(1),
  dmgBonus: z.number().optional(),
  spdBonus: z.number().optional(),
  incomeBonus: z.number().optional(),
  paths: z.array(UpgradePathDefSchema).length(2).optional(),
});

export const TowerCardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  el: ElementSchema,
  cat: TowerCategorySchema,
  cost: z.number().int().positive(),
  rarity: RaritySchema,
  stats: TowerStatsSchema,
  upgrades: z.array(TowerUpgradeSchema).length(4),
});

export const UnitCardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  el: ElementSchema,
  cost: z.number().int().positive(),
  rarity: RaritySchema,
  hp: z.number().int().positive(),
  armor: z.number().min(0).max(1),
  spd: z.number().positive(),
  income: z.number().min(0),
  dodge: z.number().min(0).max(1),
  reward: z.number().int().min(0),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});

export const StatModifierSchema = z.object({
  description: z.string().min(1),
  type: z.enum([
    'dmgBonus', 'speedBonus', 'rangeBonus', 'incomeBonus', 'hpBonus', 'costBonus', 'critBonus', 'chainBonus'
  ]),
  value: z.number(),
  targetEl: ElementSchema.optional(),
  targetCat: TowerCategorySchema.optional(),
});

export const SpellSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  effect: z.string().min(1),
  cooldownSec: z.number().positive(),
});

export const HeroSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  icon: z.string().min(1),
  class: z.enum(['offensive', 'defensive', 'economy', 'support']),
  cooldownSec: z.number().positive(),
  passivBuff: StatModifierSchema,
  passivDrawback: StatModifierSchema,
  activeSpell: SpellSchema,
});

export const ElementBuffSchema = z.object({
  el: ElementSchema,
  towerBonus: z.number().optional(),
  unitBonus: z.number().optional(),
});

export const MapConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  cols: z.number().int().positive(),
  rows: z.number().int().positive(),
  spawnX: z.number().int().min(0),
  spawnY: z.number().int().min(0),
  baseX: z.number().int().min(0),
  baseY: z.number().int().min(0),
  obstacles: z.array(z.object({ gx: z.number().int().min(0), gy: z.number().int().min(0) })),
  elementBuffs: z.array(ElementBuffSchema),
  theme: z.string().min(1),
});

export const TowersFileSchema = z.array(TowerCardSchema);

export const UnitsFileSchema = z.array(UnitCardSchema);

export const HeroesFileSchema = z.array(HeroSchema);

// ── Card Progression / Skill Tree schemas ─────────────────────────────────────

export const SkillNodeEffectSchema = z.object({
  dmgBonus:          z.number().optional(),
  spdBonus:          z.number().optional(),
  rangeBonus:        z.number().optional(),
  critBonus:         z.number().optional(),
  chainBonus:        z.number().optional(),
  splashBonus:       z.number().optional(),
  slowBonus:         z.number().optional(),
  incomeBonus:       z.number().optional(),
  hpBonus:           z.number().optional(),
  armorBonus:        z.number().optional(),
  dodgeBonus:        z.number().optional(),
  passivEnhance:     z.number().optional(),
  cooldownReduction: z.number().optional(),
});

export const SkillNodeSchema = z.object({
  id:       z.string().min(1),
  name:     z.string().min(1),
  desc:     z.string().min(1),
  requires: z.string().nullable(),
  effects:  SkillNodeEffectSchema,
  auto:     z.boolean().optional(),
});

export const SkillGateSchema = z.object({
  level: z.union([z.literal(3), z.literal(5), z.literal(7), z.literal(10)]),
  nodes: z.array(SkillNodeSchema).min(1),
});

export const CardLevelBonusesSchema = z.object({
  dmgPerLevel:               z.number().optional(),
  spdPerLevel:               z.number().optional(),
  rangePerLevel:             z.number().optional(),
  hpPerLevel:                z.number().optional(),
  armorPerLevel:             z.number().optional(),
  incomePerLevel:            z.number().optional(),
  passivEnhancePerLevel:     z.number().optional(),
  cooldownReductionPerLevel: z.number().optional(),
});

export const CardSkillTreeSchema = z.object({
  id:           z.string().min(1),
  levelBonuses: CardLevelBonusesSchema,
  gates:        z.array(SkillGateSchema),
});

export const CardSkillTreesFileSchema = z.object({
  LEVEL_THRESHOLDS: z.object({
    common:    z.array(z.number().int().positive()).length(10),
    rare:      z.array(z.number().int().positive()).length(10),
    epic:      z.array(z.number().int().positive()).length(10),
    legendary: z.array(z.number().int().positive()).length(10),
  }),
  towers: z.array(CardSkillTreeSchema),
  units:  z.array(CardSkillTreeSchema),
  heroes: z.array(CardSkillTreeSchema),
});
